import {useEffect, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {DateField} from "../../../../../../components/general/DateField.tsx";
import {GLASelector} from "../../../../../../components/specific/GLASelector";
import type {GlAccount} from "../../../../../../components/specific/GLASelector";

const OTHER_COSTS_GLA_ID = "WBedAlk";

export interface BookDifferenceValues {
    difference_booking_date: string;
    difference_booking_reason: string | null;
    difference_booking_gla_id: string | null;
}

export function BookDifferenceModal({
    isOpen,
    defaultDate,
    isSubmitting,
    glAccounts,
    onClose,
    onConfirm,
}: {
    isOpen: boolean;
    defaultDate: string;
    isSubmitting: boolean;
    glAccounts: GlAccount[];
    onClose: () => void;
    onConfirm: (values: BookDifferenceValues) => void;
}) {
    const [bookingDate, setBookingDate] = useState<string>(defaultDate);
    const [reason, setReason] = useState<string>("");
    const [selectedGla, setSelectedGla] = useState<GlAccount | null>(null);

    useEffect(() => {
        if (isOpen) {
            setBookingDate(defaultDate);
            setReason("");
            setSelectedGla(glAccounts.find((account) => account.id === OTHER_COSTS_GLA_ID) ?? null);
        }
    }, [isOpen, defaultDate, glAccounts]);

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-40">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                            Verschil wegboeken
                        </DialogTitle>

                        <div className="mt-4">
                            <GLASelector
                                label="Grootboekrekening"
                                accounts={glAccounts}
                                value={selectedGla}
                                onChange={setSelectedGla}
                            />
                        </div>

                        <div className="mt-4">
                            <DateField
                                label="Boekingsdatum verschil"
                                value={bookingDate}
                                onChange={setBookingDate}
                                id="difference-booking-date"
                            />
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm/6 font-medium text-gray-900 mb-1" htmlFor="difference-booking-reason">
                                Reden (optioneel)
                            </label>
                            <textarea
                                id="difference-booking-reason"
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm/6"
                                placeholder="Waarom wordt dit verschil weggeboekt?"
                            />
                        </div>

                        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <button
                                type="button"
                                disabled={isSubmitting || !bookingDate || !selectedGla}
                                onClick={() =>
                                    onConfirm({
                                        difference_booking_date: bookingDate,
                                        difference_booking_reason: reason.trim() === "" ? null : reason.trim(),
                                        difference_booking_gla_id: selectedGla?.id ?? null,
                                    })
                                }
                                className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-60 disabled:cursor-not-allowed sm:col-start-2"
                            >
                                {isSubmitting ? "Bezig..." : "Wegboeken"}
                            </button>
                            <button
                                type="button"
                                data-autofocus="true"
                                onClick={onClose}
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                            >
                                Terug
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
